import { Navigate, Route, Routes } from 'react-router';
import type { PublicUser } from '@gitrader/shared';
import { Spinner } from './components/ui';
import { useSession } from './lib/session';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';
import SetupPage from './pages/SetupPage';
import { treeRoutes } from './routes/router';
import { AppShell } from './shell/AppShell';
import { FullscreenProvider } from './shell/fullscreen';
import { LiveSessionProvider } from './shell/live/LiveGate';
import { SystemStatusFeed } from './shell/systemStatus';

function FullPageSpinner() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg text-ink-2">
      <Spinner />
    </div>
  );
}

/** The signed-in application: the shell with every route derived from ROUTE_TREE. */
export function SignedInApp({ user, onSignOut }: { user: PublicUser; onSignOut: () => void }) {
  return (
    <SystemStatusFeed>
      <LiveSessionProvider>
        <FullscreenProvider>
          <Routes>
            <Route element={<AppShell user={user} onSignOut={onSignOut} />}>
              {treeRoutes(user.role)}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </FullscreenProvider>
      </LiveSessionProvider>
    </SystemStatusFeed>
  );
}

export default function App() {
  const { user, needsBootstrap, isLoading, signOut } = useSession();

  if (isLoading) return <FullPageSpinner />;

  // An unclaimed instance funnels everything to the bootstrap wizard.
  if (needsBootstrap && !user) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="*" element={<Navigate to="/setup" replace />} />
      </Routes>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return <SignedInApp user={user} onSignOut={() => void signOut()} />;
}
